import React, { useState, useEffect, useCallback } from 'react';
import { CrossLandingPage } from './types';
import CrossLandingPageTable from './CrossLandingPageTable';
import CrossLandingPageForm from './CrossLandingPageForm';
import BulkCreateModal from './BulkCreateModal';
import CsvImportModal from './CsvImportModal';
import Pagination from '../JobSearch/Pagination';

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface CrossLandingPageAdminProps {
  initialData: CrossLandingPage[];
  initialPagination?: Pagination | null;
  apiBaseUrl: string;
  csrfToken: string;
}

const CrossLandingPageAdmin: React.FC<CrossLandingPageAdminProps> = ({
  initialData,
  initialPagination,
  apiBaseUrl,
  csrfToken,
}) => {
  const [crossLandingPages, setCrossLandingPages] = useState<CrossLandingPage[]>(initialData);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isBulkCreateOpen, setIsBulkCreateOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPrimaryType, setFilterPrimaryType] = useState<string>('');
  const [filterSecondaryType, setFilterSecondaryType] = useState<string>('');
  const [filterPublished, setFilterPublished] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination | null>(initialPagination || null);
  const [currentPage, setCurrentPage] = useState<number>(initialPagination?.current_page || 1);
  const [perPage, setPerPage] = useState<number>(initialPagination?.per_page || 50);
  const [sortColumn, setSortColumn] = useState<string>('display_priority');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // フィルタリングされたデータ（クライアント側フィルタリングは一時的に無効化）
  // サーバー側でページネーションを行うため、クライアント側フィルタリングは後で実装
  const filteredData = crossLandingPages;

  // データを再取得
  const refreshData = useCallback(async (page: number = currentPage) => {
    try {
      // フィルタパラメータを構築
      const queryParams = new URLSearchParams({
        per_page: perPage.toString(),
        page: page.toString(),
      });

      // 検索条件を追加（ransack形式）
      if (searchTerm) {
        // primary名、secondary名でOR検索
        queryParams.append('q[primary_name_cont]', searchTerm);
        queryParams.append('q[m]', 'or');
        queryParams.append('q[secondary_name_cont]', searchTerm);
      }
      if (filterPrimaryType) {
        queryParams.append('q[primary_type_eq]', filterPrimaryType);
      }
      if (filterSecondaryType) {
        queryParams.append('q[secondary_type_eq]', filterSecondaryType);
      }
      if (filterPublished === 'published') {
        queryParams.append('q[published_eq]', 'true');
      } else if (filterPublished === 'unpublished') {
        queryParams.append('q[published_eq]', 'false');
      }

      // ソート条件を追加
      if (sortColumn) {
        queryParams.append('q[s]', `${sortColumn} ${sortDirection}`);
      }

      const response = await fetch(`${apiBaseUrl}/index_json?${queryParams.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCrossLandingPages(data.cross_landing_pages);
        if (data.pagination) {
          setPagination(data.pagination);
          setCurrentPage(data.pagination.current_page);
        }
      }
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  }, [apiBaseUrl, csrfToken, perPage, currentPage, searchTerm, filterPrimaryType, filterSecondaryType, filterPublished, sortColumn, sortDirection]);

  // ページ変更ハンドラ
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    refreshData(page);
  }, [refreshData]);

  // フィルタ条件変更時にデータを再取得
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      refreshData(1);
    }, 300); // デバウンス（300ms）

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterPrimaryType, filterSecondaryType, filterPublished]);

  // ソート条件変更時にデータを再取得
  useEffect(() => {
    setCurrentPage(1);
    refreshData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortColumn, sortDirection]);

  // ソートハンドラ
  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      // 同じカラムの場合はソート順を切り替え
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 異なるカラムの場合は新しいカラムで昇順から開始
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  // 一括操作
  const handleBulkAction = useCallback(async (action: 'publish' | 'unpublish' | 'delete') => {
    if (selectedIds.length === 0) {
      alert('選択してください');
      return;
    }

    if (action === 'delete' && !confirm(`${selectedIds.length}件のCross Landing Pageを削除しますか？`)) {
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/bulk_action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          ids: selectedIds,
          action_type: action,
        }),
      });

      if (response.ok) {
        await refreshData();
        setSelectedIds([]);
        alert('一括操作が完了しました');
      } else {
        alert('一括操作に失敗しました');
      }
    } catch (error) {
      console.error('Bulk action failed:', error);
      alert('一括操作に失敗しました');
    }
  }, [selectedIds, apiBaseUrl, csrfToken, refreshData]);

  // CSVエクスポート
  const handleExportCsv = useCallback(() => {
    window.location.href = `${apiBaseUrl}/export_csv`;
  }, [apiBaseUrl]);

  return (
    <div className="container-fluid">
      <div className="row">
        {/* 左側：一覧エリア */}
        <div className="col-lg-7">
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <h5 className="mb-0">Cross Landing Page一覧</h5>
            </div>
            <div className="card-body">
              {/* 検索・フィルタ */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="タイトル検索"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterPrimaryType}
                    onChange={(e) => setFilterPrimaryType(e.target.value)}
                  >
                    <option value="">Primary Type: すべて</option>
                    <option value="Skill">Skill</option>
                    <option value="Occupation">Occupation</option>
                    <option value="Characteristic">Characteristic</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterSecondaryType}
                    onChange={(e) => setFilterSecondaryType(e.target.value)}
                  >
                    <option value="">Secondary Type: すべて</option>
                    <option value="Skill">Skill</option>
                    <option value="Occupation">Occupation</option>
                    <option value="Characteristic">Characteristic</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={filterPublished}
                    onChange={(e) => setFilterPublished(e.target.value)}
                  >
                    <option value="">公開: すべて</option>
                    <option value="published">公開</option>
                    <option value="unpublished">非公開</option>
                  </select>
                </div>
              </div>

              {/* 一括操作 */}
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <select
                    className="form-select"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkAction(e.target.value as 'publish' | 'unpublish' | 'delete');
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">選択したアイテムを...</option>
                    <option value="publish">公開する</option>
                    <option value="unpublish">非公開にする</option>
                    <option value="delete">削除する</option>
                  </select>
                </div>
                <div className="col-md-8">
                  <span className="text-muted">選択中: {selectedIds.length}件</span>
                </div>
              </div>

              {/* アクションボタン */}
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm me-2"
                  onClick={handleExportCsv}
                >
                  CSVをエクスポート
                </button>
                <button
                  type="button"
                  className="btn btn-outline-info btn-sm me-2"
                  onClick={() => setIsCsvImportOpen(true)}
                >
                  CSVインポート
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm me-2"
                  onClick={() => setEditingId(0)}
                >
                  新規作成
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setIsBulkCreateOpen(true)}
                >
                  一括作成
                </button>
              </div>

              {/* ページネーション情報 */}
              {pagination && (
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    全{pagination.total_count.toLocaleString()}件中 {((pagination.current_page - 1) * pagination.per_page + 1).toLocaleString()}〜{Math.min(pagination.current_page * pagination.per_page, pagination.total_count).toLocaleString()}件を表示
                  </small>
                  <div className="d-flex align-items-center gap-2">
                    <label className="text-muted small">表示件数:</label>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={perPage}
                      onChange={(e) => {
                        const newPerPage = parseInt(e.target.value, 10);
                        setPerPage(newPerPage);
                        setCurrentPage(1);
                        refreshData(1);
                      }}
                    >
                      <option value="50">50件</option>
                      <option value="100">100件</option>
                      <option value="200">200件</option>
                      <option value="500">500件</option>
                    </select>
                  </div>
                </div>
              )}

              {/* テーブル */}
              <CrossLandingPageTable
                data={filteredData}
                selectedIds={selectedIds}
                onSelectIds={setSelectedIds}
                onEdit={(id) => setEditingId(id)}
                onDelete={async (id) => {
                  if (confirm('削除しますか？')) {
                    try {
                      const response = await fetch(`${apiBaseUrl}/${id}`, {
                        method: 'DELETE',
                        headers: {
                          'Accept': 'application/json',
                          'X-CSRF-Token': csrfToken,
                        },
                      });
                      if (response.ok) {
                        await refreshData();
                        alert('削除しました');
                      } else {
                        alert('削除に失敗しました');
                      }
                    } catch (error) {
                      console.error('Delete failed:', error);
                      alert('削除に失敗しました');
                    }
                  }
                }}
                apiBaseUrl={apiBaseUrl}
                csrfToken={csrfToken}
                onRefresh={refreshData}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />

              {/* ページネーションボタン */}
              {pagination && pagination.total_pages > 1 && (
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              )}
            </div>
          </div>
        </div>

        {/* 右側：編集エリア */}
        <div className="col-lg-5">
          {editingId !== null && (
            <div className="card shadow-sm sticky-top" style={{ top: '20px' }}>
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  {editingId === 0 ? '新規作成' : '編集'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingId(null)}
                  aria-label="閉じる"
                />
              </div>
              <div className="card-body">
                <CrossLandingPageForm
                  crossLandingPage={editingId === 0 ? null : crossLandingPages.find((lp) => lp.id === editingId) || null}
                  apiBaseUrl={apiBaseUrl}
                  csrfToken={csrfToken}
                  onSave={async () => {
                    await refreshData();
                    setEditingId(null);
                  }}
                  onCancel={() => setEditingId(null)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 一括作成モーダル */}
      {isBulkCreateOpen && (
        <BulkCreateModal
          apiBaseUrl={apiBaseUrl}
          csrfToken={csrfToken}
          onClose={() => setIsBulkCreateOpen(false)}
          onSuccess={async () => {
            await refreshData();
            setIsBulkCreateOpen(false);
          }}
        />
      )}

      {/* CSVインポートモーダル */}
      {isCsvImportOpen && (
        <CsvImportModal
          apiBaseUrl={apiBaseUrl}
          csrfToken={csrfToken}
          onClose={() => setIsCsvImportOpen(false)}
          onSuccess={async () => {
            await refreshData();
            setIsCsvImportOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default CrossLandingPageAdmin;

